import React, { forwardRef, useEffect, useState } from 'react'
import { CustomModal } from '@components'
import { Box, FormControl, InputAdornment, InputLabel, MenuItem, OutlinedInput, Select, Typography, FormHelperText } from '@mui/material'
import { useDispatch } from 'react-redux'
import { apiBuyContract, apiBuyTheEurContractMinMax, apiGetMinMaxDisplay, apiGetMinMaxRaw, apiGetTheEur } from '@store'
import { showToast } from '@utils'

const Sell = forwardRef(({ handleClose, selectedContract, user }, ref) => {
    const dispatch = useDispatch()

    const [showMinMaxInput, setShowMinMaxInput] = useState(false)
    const [showVolumeError, setShowVolumeError] = useState(false)

    const [state, setState] = useState({
        market: 'THE',
        type: 'sell',
        price_selected: '',
        seller: user?.CompanyShort,
        buyer: '',
        deliverypoint: '',
        volume: '',
        exitpoint: '',
        price_percent: '100',
        ordertype: 'internal',
        currency: 'EUR',
        maxprice: '',
        on_max: false,
        minprice: '',
        on_min: false,
        sym: selectedContract?.Symbol
    })

    useEffect(() => {
        if (user?.CanSellTo?.length > 0) {
            setState((prevState) => ({
                ...prevState,
                buyer: user.CanSellTo[0]
            }))
        }
    }, [user])

    const handleChange = (e) => {
        const { name, value } = e.target
        setState({
            ...state,
            [name]: value
        })
        if (name === 'volume') {
            const vol = parseFloat(value)
            setShowVolumeError(value !== '' && !(vol > 0))
        }
    }

    const [type, setType] = useState('bid')

    const handleTypeChange = (e) => {
        setType(e.target.value)
        if (e.target.value === 'min_max') {
            setShowMinMaxInput(true)
        } else if (e.target.value === 'bid') {
            setShowMinMaxInput(false)
        }
    }

    const handleMinChange = (e) => {
        // If min price is changed, clear max price and disable it
        // If min price is cleared, enable max price
        if (e.target.value === '') {
            setState({
                ...state,
                minprice: e.target.value,
                maxprice: '',
                on_min: false,
                on_max: false
            })
        } else {
            setState({
                ...state,
                minprice: e.target.value,
                maxprice: '',
                on_min: true,
                on_max: false
            })
        }
    }

    const handleMaxChange = (e) => {
        // If max price is changed, clear min price and disable it
        // If max price is cleared, enable min price
        if (e.target.value === '') {
            setState({
                ...state,
                maxprice: e.target.value,
                minprice: '',
                on_max: false,
                on_min: false
            })
        } else {
            setState({
                ...state,
                maxprice: e.target.value,
                minprice: '',
                on_max: true,
                on_min: false
            })
        }
    }

    const handleSubmit = async (e) => {
        e.preventDefault()
        let data;
        const volume = parseFloat(state.volume);
        if (!(volume > 0)) {
            setShowVolumeError(true);
            showToast('error', 'Volume must be greater than 0');
            return;
        }
        if (type === 'bid') {
            // Remove min_max fields
            const { minprice, maxprice, on_min, on_max, ...rest } = state
            const volume = parseFloat(state.volume)
            const bid = parseFloat(selectedContract.Data.bid)

            const data = {
                ...rest,
                volume,
                price: { bid: bid }
            }

            const resp = await dispatch(apiBuyContract(data))
            if (resp?.meta?.requestStatus === 'fulfilled') {
                handleClose()
                showToast('success', 'Sales Order Saved')
            } else if (resp?.meta?.requestStatus === 'rejected') {
                showToast('error', resp?.payload || 'Error buying contract')
            }
        } else if (type === 'min_max') {
            // Remove ask fields
            data = {
                ...state,
                on_min: state.minprice ? true : false,
                on_max: state.maxprice ? true : false,
            }

            const resp = await dispatch(apiBuyTheEurContractMinMax(data))
            if (resp?.meta?.requestStatus === 'fulfilled') {
                handleClose()
                dispatch(apiGetMinMaxRaw())
                dispatch(
                    apiGetMinMaxDisplay({ product: 'THE', currency: 'EUR' })
                )
                dispatch(apiGetTheEur())
                showToast('success', 'Sales Order Saved')
            } else if (resp?.meta?.requestStatus === 'rejected') {
                showToast('error', resp?.payload || 'Error buying contract')
            }
        }
    }

    // timer
    const [timer, setTimer] = useState(25);

    useEffect(() => {
        const interval = setInterval(() => {
            setTimer(timer => timer - 1);
        }, 1000);
        return () => clearInterval(interval);
    }, [timer]);

    useEffect(() => {
        if (timer === 0) {
            handleClose();
        }
    }, [timer, handleClose]);

    useEffect(() => {
        if (ref?.current) {
            setTimer(25);
        }
    }, [ref]);

    return (
        <CustomModal
            handleClose={handleClose}
            handleSubmit={handleSubmit}
            timer={timer}
            modalTitle={`Sell Contract`}
            modalTitleColor={'#EB6969'}
            buttonText={'Sell'}
            className="modal-button-danger"
            disabled={!(parseFloat(state.volume) > 0)}
            ref={ref}
        >
            <Box className="flex flex-col gap-3">
                <FormControl fullWidth>
                    <InputLabel
                        id="demo-simple-select-label"
                        sx={{ bgcolor: '#F5F5F5', pr: 1 }}
                    >
                        Please Select what you would like to sell
                    </InputLabel>
                    <Select
                        labelId="demo-simple-select-label"
                        id="demo-simple-select"
                        label="Type"
                        name="type"
                        value={type}
                        onChange={handleTypeChange}
                    >
                        <MenuItem value="bid">Bid Price</MenuItem>
                        <MenuItem value="min_max">Min/Max Stop</MenuItem>
                    </Select>
                </FormControl>
                <Box className="flex gap-3">
                    <Typography>Contract:</Typography>
                    <Typography color={'black'}>
                        {selectedContract.Symbol}
                    </Typography>
                </Box>
                <FormControl fullWidth>
                    <InputLabel
                        id="demo-simple-select-label"
                        sx={{ bgcolor: '#F5F5F5', pr: 1 }}
                    >
                        Sell To
                    </InputLabel>
                    <Select
                        labelId="demo-simple-select-label"
                        id="demo-simple-select"
                        label="Sell To"
                        name="buyer"
                        value={state.buyer}
                        onChange={handleChange}
                    >
                        <MenuItem value={'SET'}>SET</MenuItem>
                    </Select>
                </FormControl>
                <FormControl fullWidth variant="outlined" error={showVolumeError}>
                    <InputLabel
                        id="contractLabel"
                        sx={{ bgcolor: '#F5F5F5', p: '0 3px' }}
                    >
                        Volume
                    </InputLabel>
                    <OutlinedInput
                        id="volume"
                        label="Volume"
                        type="number"
                        name="volume"
                        value={state.volume}
                        onChange={handleChange}
                        inputProps={{
                            step: 0.01,
                            min: 0
                        }}
                        required
                        endAdornment={
                            <InputAdornment position="start">MW</InputAdornment>
                        }
                    />
                    <FormHelperText>
                        {showVolumeError ? 'Volume must be greater than 0' : ''}
                    </FormHelperText>
                </FormControl>
                <Box className="flex gap-3">
                    <Typography>Price:</Typography>
                    <Typography color={'black'}>
                        {selectedContract.Data.bid}
                    </Typography>
                </Box>
                {/* <Box className='flex gap-3'>
                    <Typography>Settlement Amount:</Typography>
                    <Typography color={'black'}>{selectedContract.Data.bid * state?.volume || 0} </Typography >
                </Box> */}
                {showMinMaxInput && (
                    <Box className="flex gap-3">
                        <FormControl fullWidth variant="outlined">
                            <InputLabel
                                id="priceLabel"
                                sx={{ bgcolor: '#F5F5F5', p: '0 3px' }}
                            >
                                Max Price
                            </InputLabel>
                            <OutlinedInput
                                id="price"
                                label="Price"
                                type="number"
                                name="maxprice"
                                value={state.maxprice}
                                onChange={handleMaxChange}
                                disabled={state.on_min}
                                required
                            />
                        </FormControl>
                        <FormControl fullWidth variant="outlined">
                            <InputLabel
                                id="priceLabel"
                                sx={{ bgcolor: '#F5F5F5', p: '0 3px' }}
                            >
                                Min Price
                            </InputLabel>
                            <OutlinedInput
                                id="price"
                                label="Price"
                                type="number"
                                name="minprice"
                                value={state.minprice}
                                onChange={handleMinChange}
                                disabled={state.on_max}
                                required
                            />
                        </FormControl>
                    </Box>
                )}
                <Box className="flex gap-3">
                    <Typography>Last Updated:</Typography>
                    <Typography color={'black'}>{}</Typography>
                </Box>
            </Box>
        </CustomModal>
    );
})

export default Sell